import { normalizeEntityType } from "../types";
import type { EntityType, EventOption } from "../types";
import { adoptFlow, withFlow } from "../utils/activityFlow";

/**
 * Default event options per entity type, derived from migration data
 * (entity_type_event_options_association.json + event_option.json).
 *
 * Entity types not listed here get COMMON_EVENT_OPTIONS as a fallback.
 *
 * Each list is paired with a flow — the steps that can open the activity, and
 * what each step can lead to. `withFlow` attaches that wiring to the options
 * themselves, so an entity that copies these defaults copies the whole
 * workflow with them. See `utils/activityFlow` for how it's read back, and
 * remember that a flow only decides which steps get a one-tap button: every
 * option stays reachable under "More…".
 */

// Common fallback used when no entity is linked or entity type has no defaults
export const COMMON_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "common-start", displayName: "Started Waiting", eventType: "WAIT_START", category: "START" },
    { id: "common-active", displayName: "Started", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "common-end", displayName: "Finished", eventType: "MAIN_EVENT_END", category: "END" },
  ],
  {
    // A generic activity is a straight line: the wait ends when the thing
    // starts, and it can't finish before it has.
    "common-start": { start: true, next: ["common-active"] },
    "common-active": { start: true, next: ["common-end"] },
    "common-end": { next: [] },
  },
);

// A queue is the branchiest thing in the app, and no two rides agree on the
// order. Hagrid's lockers are outside the main line, so it goes lockers, line,
// inside; VelociCoaster's are in the queue, so it goes line, lockers, on; and
// most rides have no lockers to stop at. The type can't know which, so it
// declares that any part of a queue can follow any other and the ride is always
// one tap away — a mesh, not a sequence. The order below is only which of them
// gets offered first.
//
// The ride itself is what narrows it, and that's where the real answer lives:
//   Hagrid          "Entered Line" → the lockers alone; "On Ride" requires them
//   VelociCoaster   "Main Line" → the lockers alone; "On Ride" requires them
//   no lockers      delete the step; edges pointing at it are dropped
// See the Hagrid and VelociCoaster cases in `utils/activityFlow.test.ts`.
const ATTRACTION_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "attr-entered-line", displayName: "Entered Line", eventType: "WAIT_START", category: "START" },
    { id: "attr-main-line", displayName: "Main Line", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "attr-inside", displayName: "Inside", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "attr-at-lockers", displayName: "At Lockers", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "attr-preshow-start", displayName: "Preshow Start", eventType: "PRESHOW_START", category: "MILESTONE" },
    { id: "attr-preshow-end", displayName: "Preshow End", eventType: "PRESHOW_END", category: "MILESTONE" },
    { id: "attr-on-ride", displayName: "On Ride", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "attr-exit-ride", displayName: "Exit Ride", eventType: "MAIN_EVENT_END", category: "END" },
    { id: "attr-delay-start", displayName: "Delay Start", eventType: "DELAY_START", category: "DELAY_START" },
    { id: "attr-delay-end", displayName: "Delay End", eventType: "DELAY_END", category: "DELAY_END" },
  ],
  {
    // Outdoor switchbacks, then the building, then the lockers by the station,
    // then the preshow — the usual shape of a Universal queue. Each step still
    // offers the ride itself, because plenty of queues skip most of this.
    "attr-entered-line": {
      start: true,
      next: ["attr-main-line", "attr-inside", "attr-at-lockers", "attr-preshow-start", "attr-on-ride"],
    },
    "attr-main-line": { next: ["attr-inside", "attr-at-lockers", "attr-preshow-start", "attr-on-ride"] },
    "attr-inside": { next: ["attr-at-lockers", "attr-main-line", "attr-preshow-start", "attr-on-ride"] },
    // Back out to the switchbacks after the lockers is Hagrid's whole shape,
    // so the main line has to be reachable from here.
    "attr-at-lockers": { next: ["attr-main-line", "attr-inside", "attr-preshow-start", "attr-on-ride"] },
    // Missing the end of a preshow shouldn't strand you off the ride.
    "attr-preshow-start": { next: ["attr-preshow-end", "attr-on-ride"] },
    "attr-preshow-end": {
      requires: ["attr-preshow-start"],
      next: ["attr-on-ride", "attr-inside", "attr-at-lockers"],
    },
    // Walk-ons exist, so riding can also be the first thing logged.
    "attr-on-ride": { start: true, next: ["attr-exit-ride"] },
    "attr-exit-ride": { next: [] },
  },
);

// Anything with a start time you watch or take part in: a show or parade, a
// round of cruise trivia, a tasting, a tour, a shore excursion. Shows and
// excursions were their own types until October 2026; the place's features
// decide which of the lead-up steps it has (a queue, a check in, a room to go
// into, seats, a preshow), and "Started" is always one tap away, because a
// street show is often running before you think to log it.
const EXPERIENCE_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "exp-entered-line", displayName: "Entered Line", eventType: "WAIT_START", category: "START" },
    { id: "exp-check-in", displayName: "Checked In", eventType: "CHECK_IN", category: "MILESTONE" },
    { id: "exp-inside", displayName: "Inside", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "exp-seated", displayName: "Seated", eventType: "SEATED", category: "MILESTONE" },
    { id: "exp-preshow-start", displayName: "Preshow Start", eventType: "PRESHOW_START", category: "MILESTONE" },
    { id: "exp-preshow-end", displayName: "Preshow End", eventType: "PRESHOW_END", category: "MILESTONE" },
    { id: "exp-started", displayName: "Started", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "exp-ended", displayName: "Ended", eventType: "MAIN_EVENT_END", category: "END" },
    { id: "exp-delay-start", displayName: "Delay Start", eventType: "DELAY_START", category: "DELAY_START" },
    { id: "exp-delay-end", displayName: "Delay End", eventType: "DELAY_END", category: "DELAY_END" },
  ],
  {
    "exp-entered-line": { start: true, next: ["exp-check-in", "exp-inside", "exp-seated", "exp-started"] },
    "exp-check-in": { start: true, next: ["exp-inside", "exp-seated", "exp-started"] },
    "exp-inside": { start: true, next: ["exp-seated", "exp-preshow-start", "exp-started"] },
    "exp-seated": { start: true, next: ["exp-preshow-start", "exp-started"] },
    "exp-preshow-start": { next: ["exp-preshow-end", "exp-started"] },
    "exp-preshow-end": { requires: ["exp-preshow-start"], next: ["exp-started"] },
    "exp-started": { start: true, next: ["exp-ended"] },
    "exp-ended": { next: [] },
  },
);

// A meal can begin four ways, and which one depends on the restaurant: the
// podium, walking in, sitting down — or a mobile order placed from a bench
// somewhere else entirely, half an hour before you set off. So Order Placed is
// a starting step too, and it leads back to the arriving steps rather than
// assuming you were already there when you ordered.
//
// Courses arrive in waves, so the flow also loops: Order Received leads back to
// Order Placed first, then to the bill. Inside and Seated happen once a meal —
// a mobile order can still lead to the table, but only until you've sat down.
//
// This is every place you eat or drink — a bar or a coffee cart as much as a
// dining room (lounges were their own type until October 2026) — so the end is
// plain "Done", and a take-away order ends at "Picked Up": the order handed
// over and the visit over in one tap, still timed from Order Placed.
const RESTAURANT_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "rest-entered-line", displayName: "Entered Line", eventType: "WAIT_START", category: "START" },
    { id: "rest-inside", displayName: "Inside", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "rest-seated", displayName: "Seated", eventType: "SEATED", category: "MILESTONE" },
    { id: "rest-order-placed", displayName: "Order Placed", eventType: "ORDER_PLACED", category: "MILESTONE" },
    { id: "rest-order-received", displayName: "Order Received", eventType: "ORDER_RECEIVED", category: "MILESTONE" },
    { id: "rest-done-dining", displayName: "Done", eventType: "DINING_DONE", category: "END" },
    { id: "rest-picked-up", displayName: "Picked Up", eventType: "PICKED_UP", category: "END" },
  ],
  {
    "rest-entered-line": { start: true, next: ["rest-inside", "rest-seated"] },
    "rest-inside": { start: true, once: true, next: ["rest-seated", "rest-order-placed"] },
    "rest-seated": { start: true, once: true, next: ["rest-order-placed"] },
    "rest-order-placed": {
      start: true,
      next: [
        "rest-order-received",
        "rest-seated",
        "rest-inside",
        // The mobile-order pickup queue, which is a wait worth measuring.
        "rest-entered-line",
        "rest-picked-up",
        "rest-done-dining",
      ],
    },
    "rest-order-received": {
      requires: ["rest-order-placed"],
      next: ["rest-order-placed", "rest-done-dining", "rest-seated"],
    },
    "rest-done-dining": { next: [] },
    "rest-picked-up": { requires: ["rest-order-placed"], next: [] },
  },
);

const PARK_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    // Order matters: it decides which of the starting steps leads the row, and
    // how "More…" lists them. Arriving comes first in both readings.
    { id: "park-arrived", displayName: "Arrived", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "park-at-security", displayName: "At Security", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "park-inside", displayName: "Inside", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "park-left", displayName: "Left", eventType: "MAIN_EVENT_END", category: "END" },
  ],
  {
    "park-at-security": { start: true, next: ["park-arrived", "park-inside"] },
    "park-arrived": { start: true, next: ["park-at-security", "park-inside", "park-left"] },
    // Remembering the park hours after you are already through the gates.
    "park-inside": { start: true, next: ["park-left"] },
    "park-left": { next: [] },
  },
);

// Hotels carry the theme-park heritage step (resorts there have a security
// check on the way in) plus the stay's own arc: the check-in line, the room,
// and bags left with the desk. "Arrived" keeps MAIN_EVENT_START — it's what
// already-logged stays recorded — but sits in the START group so it's a first
// tap alongside the check-in line, which "Checked In" closes.
const HOTEL_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "hotel-arrived", displayName: "Arrived", eventType: "MAIN_EVENT_START", category: "START" },
    { id: "hotel-at-security", displayName: "At Security", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "hotel-check-in-line", displayName: "Check In Line", eventType: "WAIT_START", category: "START" },
    // WAIT_END, so the check-in line measures as a wait — but grouped as a
    // milestone so it stays a one-tap step after the arrival is logged.
    { id: "hotel-checked-in", displayName: "Checked In", eventType: "WAIT_END", category: "MILESTONE" },
    { id: "hotel-checked-bags", displayName: "Checked Bags", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "hotel-in-room", displayName: "In Room", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "hotel-left-room", displayName: "Left Room", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    // A milestone, not the end: bags often wait at the bell desk while the
    // last day goes on, and "Left" is what closes the stay.
    { id: "hotel-checked-out", displayName: "Checked Out", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "hotel-left", displayName: "Left", eventType: "MAIN_EVENT_END", category: "END" },
  ],
  {
    "hotel-at-security": { start: true, next: ["hotel-arrived", "hotel-check-in-line"] },
    "hotel-arrived": {
      start: true,
      // "Left" stays reachable from every step of the stay — dropping bags and
      // heading straight back out is a real morning, and an activity you can't
      // close from where you're standing is a trap.
      next: [
        "hotel-check-in-line",
        "hotel-checked-bags",
        "hotel-in-room",
        "hotel-at-security",
        "hotel-left",
      ],
    },
    "hotel-check-in-line": { start: true, next: ["hotel-checked-in"] },
    // A stay is a loop, not a line: you come and go from the room for days
    // before the one "Left" that ends it.
    "hotel-checked-in": {
      requires: ["hotel-check-in-line"],
      next: ["hotel-in-room", "hotel-checked-bags", "hotel-checked-out", "hotel-left"],
    },
    "hotel-checked-bags": { next: ["hotel-in-room", "hotel-check-in-line", "hotel-checked-out", "hotel-left"] },
    "hotel-in-room": { next: ["hotel-left-room", "hotel-checked-out", "hotel-left"] },
    "hotel-left-room": { next: ["hotel-in-room", "hotel-checked-out", "hotel-left"] },
    "hotel-checked-out": { next: ["hotel-left", "hotel-checked-bags"] },
    "hotel-left": { next: [] },
  },
);

// Something you go to that holds other things you log: Halloween Horror
// Nights, a Disney party, a festival or a convention. It's a visit like a
// park day, so it has the park's steps.
const EVENT_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "event-arrived", displayName: "Arrived", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "event-at-security", displayName: "At Security", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "event-inside", displayName: "Inside", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "event-left", displayName: "Left", eventType: "MAIN_EVENT_END", category: "END" },
  ],
  {
    "event-at-security": { start: true, next: ["event-arrived", "event-inside"] },
    "event-arrived": { start: true, next: ["event-at-security", "event-inside", "event-left"] },
    "event-inside": { start: true, next: ["event-left"] },
    "event-left": { next: [] },
  },
);

const TRANSPORTATION_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "trans-check-in", displayName: "Check In", eventType: "CHECK_IN", category: "MILESTONE" },
    { id: "trans-security", displayName: "Security", eventType: "SECURITY", category: "MILESTONE" },
    { id: "trans-boarding", displayName: "Boarding", eventType: "BOARDING", category: "MILESTONE" },
    { id: "trans-departed", displayName: "Departed", eventType: "DEPARTED", category: "ACTIVE" },
    { id: "trans-landed", displayName: "Arrived", eventType: "LANDED", category: "END" },
  ],
  {
    // Every step of the airport is skippable — carry-on only, pre-check, or
    // just remembering to open the app once you are already in your seat.
    "trans-check-in": { start: true, next: ["trans-security", "trans-boarding"] },
    "trans-security": { start: true, next: ["trans-boarding", "trans-departed"] },
    "trans-boarding": { start: true, next: ["trans-departed"] },
    "trans-departed": { start: true, next: ["trans-landed"] },
    "trans-landed": { next: [] },
  },
);

const SHIP_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "ship-check-in", displayName: "Check In", eventType: "CHECK_IN", category: "MILESTONE" },
    { id: "ship-security", displayName: "Security", eventType: "SECURITY", category: "MILESTONE" },
    { id: "ship-boarding", displayName: "Boarding", eventType: "BOARDING", category: "MILESTONE" },
    { id: "ship-departed", displayName: "Departed", eventType: "DEPARTED", category: "ACTIVE" },
    { id: "ship-arrived", displayName: "Arrived", eventType: "LANDED", category: "END" },
  ],
  {
    "ship-check-in": { start: true, next: ["ship-security", "ship-boarding"] },
    "ship-security": { start: true, next: ["ship-boarding", "ship-departed"] },
    "ship-boarding": { start: true, next: ["ship-departed"] },
    "ship-departed": { start: true, next: ["ship-arrived"] },
    "ship-arrived": { next: [] },
  },
);

// An airport is two different visits that share a building. Flying out, you
// arrive, maybe queue to check a bag, clear security and board; landing, you
// start at Off Plane, maybe wait at the carousel, and leave. The flight
// itself is its own activity beside the airport, not inside it, so boarding
// is where this one ends. Steps are from the generic "Airport" place that was
// built up by hand before airports had a type of their own.
const AIRPORT_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "airport-arrived", displayName: "Arrived", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "airport-line", displayName: "Entered Line", eventType: "WAIT_START", category: "START" },
    { id: "airport-checked-in", displayName: "Checked In", eventType: "WAIT_END", category: "ACTIVE" },
    { id: "airport-bag-drop", displayName: "Bag Drop", eventType: "WAIT_END", category: "ACTIVE" },
    { id: "airport-security-line", displayName: "Security Line", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "airport-through-security", displayName: "Through Security", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "airport-boarded", displayName: "Boarded Plane", eventType: "MAIN_EVENT_END", category: "END" },
    { id: "airport-off-plane", displayName: "Off Plane", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "airport-baggage-claim", displayName: "At Baggage Claim", eventType: "WAIT_START", category: "START" },
    { id: "airport-first-bag", displayName: "First Bag on Carousel", eventType: "PROGRESS_UPDATE", category: "MILESTONE" },
    { id: "airport-received-bags", displayName: "Received Bags", eventType: "WAIT_END", category: "ACTIVE" },
    { id: "airport-left", displayName: "Left", eventType: "MAIN_EVENT_END", category: "END" },
  ],
  {
    "airport-arrived": {
      start: true,
      next: [
        "airport-line",
        "airport-security-line",
        "airport-checked-in",
        "airport-bag-drop",
        "airport-baggage-claim",
        "airport-left",
      ],
    },
    "airport-line": { next: ["airport-checked-in", "airport-bag-drop", "airport-security-line"] },
    "airport-checked-in": { next: ["airport-bag-drop", "airport-security-line"] },
    "airport-bag-drop": { next: ["airport-security-line"] },
    "airport-security-line": { next: ["airport-through-security", "airport-boarded"] },
    "airport-through-security": { next: ["airport-boarded", "airport-left"] },
    "airport-boarded": { requires: ["airport-arrived"], next: [] },
    // Landing starts with stepping off the plane rather than walking in.
    "airport-off-plane": {
      start: true,
      next: ["airport-baggage-claim", "airport-first-bag", "airport-received-bags", "airport-left"],
    },
    "airport-baggage-claim": { next: ["airport-first-bag", "airport-received-bags", "airport-left"] },
    "airport-first-bag": { next: ["airport-received-bags", "airport-left"] },
    "airport-received-bags": { next: ["airport-left"] },
    "airport-left": { next: [] },
  },
);

// Somewhere you simply stopped: a store, a port, an office, Disney Springs.
// You arrived and you left. A shop's register queue (Checking Out → Paid)
// brackets the wait the way a hotel's check-in line does; shops and ports were
// their own types until October 2026, and the register is a feature a place
// turns on.
const LOCATION_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "location-arrived", displayName: "Arrived", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "location-checking-out", displayName: "Checking Out", eventType: "WAIT_START", category: "START" },
    { id: "location-paid", displayName: "Paid", eventType: "WAIT_END", category: "MILESTONE" },
    { id: "location-left", displayName: "Left", eventType: "MAIN_EVENT_END", category: "END" },
  ],
  {
    "location-arrived": { start: true, next: ["location-checking-out", "location-left"] },
    "location-checking-out": { start: true, next: ["location-paid"] },
    // Browsing on after paying is normal, so the register can come round again.
    "location-paid": { requires: ["location-checking-out"], next: ["location-left", "location-checking-out"] },
    "location-left": { next: [] },
  },
);

const DESTINATION_EVENT_OPTIONS: EventOption[] = withFlow(
  [
    { id: "dest-arrived", displayName: "Arrived", eventType: "MAIN_EVENT_START", category: "ACTIVE" },
    { id: "dest-left", displayName: "Left", eventType: "MAIN_EVENT_END", category: "END" },
  ],
  {
    "dest-arrived": { start: true, next: ["dest-left"] },
    "dest-left": { next: [] },
  },
);

export const DEFAULT_EVENT_OPTIONS: Record<EntityType, EventOption[]> = {
  DESTINATION: DESTINATION_EVENT_OPTIONS,
  PARK: PARK_EVENT_OPTIONS,
  EVENT: EVENT_EVENT_OPTIONS,
  HOTEL: HOTEL_EVENT_OPTIONS,
  SHIP: SHIP_EVENT_OPTIONS,
  AIRPORT: AIRPORT_EVENT_OPTIONS,
  ATTRACTION: ATTRACTION_EVENT_OPTIONS,
  EXPERIENCE: EXPERIENCE_EVENT_OPTIONS,
  RESTAURANT: RESTAURANT_EVENT_OPTIONS,
  TRANSPORTATION: TRANSPORTATION_EVENT_OPTIONS,
  LOCATION: LOCATION_EVENT_OPTIONS,
};

/** A type's built-in steps; a type saved before the October 2026 merge gets its new type's. */
function builtInOptionsFor(entityType: EntityType | string | null | undefined): EventOption[] {
  const type = normalizeEntityType(entityType);
  return type ? DEFAULT_EVENT_OPTIONS[type] : COMMON_EVENT_OPTIONS;
}

/**
 * Get event options for an entity, falling back to its type's: the ones saved
 * in the app, else the built-in ones above.
 */
export function getEventOptionsForEntity(
  entityEventOptions: EventOption[] | undefined,
  entityType: EntityType | string | undefined,
  /** The type's steps as saved in the app (`typeSettings/{TYPE}`), if any. */
  typeEventOptions?: EventOption[] | null,
): EventOption[] {
  const defaults =
    typeEventOptions && typeEventOptions.length > 0
      ? typeEventOptions
      : builtInOptionsFor(entityType);
  // If the entity has its own event options, use those — but lend them the
  // type's flow when they carry none of their own. Every migrated entity has
  // its own copy of the steps, so without this almost nothing would have a
  // flow. An entity that has been wired up by hand keeps its own wiring.
  if (entityEventOptions && entityEventOptions.length > 0) {
    return adoptFlow(entityEventOptions, defaults);
  }
  return defaults;
}
